import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { Skeleton } from '../../components/ui';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '../../hooks/useNotifications';
import { formatUsd } from '../../lib/format';
import type { NotificationItem } from '../../lib/types';
import { CabEmpty, Panel } from '../parts';
import { CabTabs } from '../tabs';

function relTime(iso: string, ru: boolean): string {
  const m = Math.floor((Date.now() - Date.parse(iso)) / 60000);
  if (!Number.isFinite(m)) return '';
  if (m < 1) return ru ? 'только что' : 'now';
  if (m < 60) return `${m}${ru ? ' мин' : 'm'}`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}${ru ? ' ч' : 'h'}`;
  return `${Math.floor(h / 24)}${ru ? ' дн' : 'd'}`;
}

function title(kind: string, t: (k: string) => string): string {
  const key = `cabinet.kind_${kind.replace(/[.]/g, '_')}`;
  const label = t(key);
  return label === key ? kind.replace(/[._]/g, ' ') : label;
}

export function CabNotifications() {
  const { t, i18n } = useTranslation();
  const ru = i18n.language.startsWith('ru');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const q = useNotifications(unreadOnly);
  const markAll = useMarkAllNotificationsRead();
  const markOne = useMarkNotificationRead();
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <CabTabs
          value={unreadOnly ? 'unread' : 'all'}
          onChange={(v) => setUnreadOnly(v === 'unread')}
          options={[
            { value: 'all', label: t('cabinet.filter_all') },
            { value: 'unread', label: t('cabinet.filter_unread') },
          ]}
        />
        <button
          type="button"
          onClick={() => markAll.mutate()}
          disabled={markAll.isPending}
          className="pb-focus-ring rounded-pb-md px-3 py-1.5 text-[13px] font-medium text-pb-accent hover:bg-pb-accent-soft disabled:opacity-50"
        >
          {t('cabinet.mark_all_read')}
        </button>
      </div>

      <Panel>
        {q.isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-14 rounded-pb-md" />
            ))}
          </div>
        ) : items.length ? (
          <div className="divide-y divide-pb-border-soft">
            {items.map((n) => (
              <Row
                key={n.id}
                n={n}
                ru={ru}
                t={t}
                onRead={() =>
                  !n.read_at && markOne.mutate({ notificationId: n.id })
                }
              />
            ))}
          </div>
        ) : (
          <CabEmpty msg={t('cabinet.no_notifications')} />
        )}

        {q.hasNextPage ? (
          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => q.fetchNextPage()}
              disabled={q.isFetchingNextPage}
              className="pb-focus-ring rounded-pb-md border border-pb-border-soft px-4 py-2 text-[13px] font-medium text-tg-hint hover:bg-pb-paper2 hover:text-tg-text disabled:opacity-50"
            >
              {t('cabinet.load_more')}
            </button>
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

function Row({
  n,
  ru,
  t,
  onRead,
}: {
  n: NotificationItem;
  ru: boolean;
  t: (k: string) => string;
  onRead: () => void;
}) {
  const amt = (n.payload as { amount_usd?: string }).amount_usd;
  const unread = !n.read_at;
  return (
    <button
      type="button"
      onClick={onRead}
      className="flex w-full items-center gap-3 py-3 text-left"
    >
      <span
        className={clsx(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[15px]',
          unread ? 'bg-pb-accent-soft text-pb-accent' : 'bg-pb-paper2 text-tg-hint',
        )}
      >
        🔔
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[14px] text-tg-text">
          {title(n.kind, t)}
        </div>
        <div className="pb-caption mt-0.5">{relTime(n.created_at, ru)}</div>
      </div>
      {amt != null ? (
        <span className="pb-numeral text-[13px] text-tg-text">{formatUsd(amt)}</span>
      ) : null}
      {unread ? (
        <span className="h-2 w-2 shrink-0 rounded-full bg-pb-accent" />
      ) : null}
    </button>
  );
}
